import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {toast} from 'react-toastify';
import { AppService } from 'shared/services';
import AppBar from '@mui/material/AppBar';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Download from './download';
import Upload from './upload';
import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RCLogo from 'assets/images/rc-logo.svg'
import dashboardStyles from './style';
import { globalStyles, noop, sessionExpireMsg } from 'shared/constants';
import { getToken, getTokenData, removeToken } from 'utils';
import Grid from './grid';
import InputAdornment from '@mui/material/InputAdornment';
import CheckIcon from "@mui/icons-material/Check";
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Button from '@mui/material/Button';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import ErrorIcon from 'assets/images/error-icon.svg';
import { About } from 'shared/components';
import ToastDialog from 'shared/components/toast-dialog';

const DRAWER_POSITION = 'right';
const defaultState = {
  drawerOpen: false,
};

const DashboardView = ({
  uid = '',
  jwksToken = null,
  isLoading = false,
  isUidIncorrect,
  accountName = '',
  errors = {},
  isSyncing = false,
  syncErrors = [],
  syncingProgress = 0,
  syncDialog = {},
  isTemplateDownloaded = false,
  isSyncCompleted = false,
  gridData = [],
  version = {},
  isErrorEncountered = false,
  toastDialog = {},
  status = '',
  handleKeyDown = noop,
  handleBlur = noop,
  handleChange = noop,
  onTemplateDownload = noop,
  handleSync = noop,
  handleSyncDialogClose = noop,
  onContinue = noop,
  onToastClose = noop,
}) => {
  let navigate = useNavigate();
  const [state, setState] = useState(defaultState);
  const tokenData = getTokenData();

  const toggleDrawer = open => evt => {
    const { type, key } = evt;
    setState(prevState => ({
      ...prevState,
      drawerOpen: type === 'keydown' && (key === 'Tab' || key === 'Shift') ? prevState.drawerOpen : open,
    }));
  };

  const logoutUser = async () => {
    const { error } = await AppService.logoutUser(getToken());
    if (!error) {
      removeToken();
      navigate('/');
    } else {
      toast.error(sessionExpireMsg);
    }
  };

  const getInputAdorment = () => {
    if (!isLoading && !isUidIncorrect && !accountName) {
      return <InputAdornment></InputAdornment>;
    }
    return <InputAdornment>{isLoading ? <CircularProgress sx={{ ...dashboardStyles.circularProgress }}  size={24} /> : isUidIncorrect ? <CloseIcon color="error" /> : <CheckIcon color="success"/>}</InputAdornment>;
  };

  return (
    <Box className='000'>
      <AppBar position="static" sx={dashboardStyles.appBar} elevation={0}>
        <Toolbar>
          <Box sx={{
            ...globalStyles.display.flex,
            ...globalStyles.flex.justify.between,
            ...globalStyles.width[100]
          }}>
            <Box
              sx={{ ...globalStyles.cursor.pointer }}
            >
              <img
                alt="RC Logo"
                src={RCLogo}
                onClick={() => window.location.reload()}
              />
            </Box>
            <Avatar
              alt="Avatar"
              src="#"
              sx={globalStyles.cursor.pointer}
              onClick={toggleDrawer(true)}
            />
          </Box>
        </Toolbar>
      </AppBar>
      <Drawer
        anchor={DRAWER_POSITION}
        open={state.drawerOpen}
        onClose={toggleDrawer(false)}
      >
        <Box
          sx={{ width: 250 }}
          role="presentation"
          onClick={toggleDrawer(false)}
          onKeyDown={toggleDrawer(false)}
        >
          <List>
            <Box
              sx={{
                ...globalStyles.display.flex,
                ...globalStyles.flex.align.center,
                ...globalStyles.padding(2),
                backgroundColor: 'grey.200'
              }}
            >
              <Avatar
                alt="Avatar"
                src="#"
                sx={dashboardStyles.avatar}
                onClick={toggleDrawer(true)}
              />
              <Box style={{ ...globalStyles.padding(16, 'Left')}} >
                <Typography variant='body2' style={{...globalStyles.text.weight.bold}}>
                  {`${tokenData?.firstName} ${tokenData?.lastName}`}
                </Typography>
              </Box>
            </Box>
            <About version={version} />
            <ListItem
              button
              onClick={logoutUser}
            >
              <ListItemText primary={<Typography variant="body2">Sign Out</Typography>} />
              <ListItemIcon sx={{ ...globalStyles.display.flex, ...globalStyles.flex.justify.end }}>
                <ArrowForwardOutlinedIcon />
              </ListItemIcon>
            </ListItem>
          </List>
        </Box>
      </Drawer>
      <Box component="main" sx={{
          ...globalStyles.padding(4, 'Left'),
          ...globalStyles.padding(4, 'Right')
        }}
      >
        <Box sx={{...globalStyles.padding(2, 'Top')}}>
          <Paper elevation={0} sx={{...globalStyles.padding(4)}}>
            <Typography variant="body1" sx={{...globalStyles.text.weight.bold}}>Emergency Response Locations</Typography>
            <Typography variant="body2">This is a section of body text where you can enter some stuff.</Typography>
            <Typography
              variant="body2"
              sx={{
                ...globalStyles.text.weight.bold,
                ...globalStyles.margin(4, 'Top'),
                ...globalStyles.margin(1, 'Bottom')
              }}
            >
              Enter the UID
            </Typography>
            <TextField
              sx={dashboardStyles.textField}
              size="small"
              type='text'
              name='uid'
              variant="outlined"
              placeholder='Target Account (UID)'        
              value={!!accountName ? `${uid} - ${accountName}` : `${uid}`}
              error={!!errors.uid.trim()}
              helperText={errors.uid}
              InputProps={{
                endAdornment: getInputAdorment()
              }}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
            />
          </Paper>
        </Box>
        {!!jwksToken && (
          <Box sx={{...globalStyles.padding(2, 'Top')}}>
            <Download isTemplateDownloaded={isTemplateDownloaded} onTemplateDownload={onTemplateDownload} />
          </Box>
        )}
        {!!jwksToken && (
          <Box sx={{...globalStyles.padding(2, 'Top')}}>
            <Upload
              isSyncCompleted={isSyncCompleted}
              isSyncing={isSyncing}
              syncingProgress={syncingProgress}
              syncErrors={syncErrors}
              handleSync={handleSync}
              onContinue={onContinue}
            />
          </Box>
        )}
        {(syncingProgress >= 100 && !syncErrors.length) && (
          <Box sx={{...globalStyles.padding(2, 'Top')}}>
            <Grid
              data={gridData}
            />
          </Box>
        )}
        {syncingProgress >= 100 && (
          <Box sx={{...globalStyles.padding(2, 'Top')}}>
            <Paper elevation={0} sx={{ ...globalStyles.padding(4) }}>
              <Box
                sx={{
                  ...globalStyles.display.flex,
                  ...globalStyles.flex.justify.between
                }}
              >
                <Typography
                  variant="body2"
                  sx={{...(!syncErrors.length ? dashboardStyles.reuploadLabel : {})}}
                >
                  5. Correct errors in the Excel Sheet and re-upload.
                </Typography>
                {!syncErrors.length && (
                  <Button
                    variant="contained"
                    onClick={() => {
                      window.location.reload();
                    }}
                  >
                    Home
                  </Button>
                )}
              </Box>
            </Paper>
          </Box>
        )}
      </Box>
      {syncDialog.open && (
        <Dialog
          open={syncDialog.open}
          onClose={handleSyncDialogClose}
          sx={{...dashboardStyles.paper}}
        >
          <DialogContent>
            <Box
              sx={{
                ...globalStyles.display.flex,
                ...globalStyles.flex.justify.end,
                ...globalStyles.cursor.pointer
              }}
            >
              <CloseIcon onClick={handleSyncDialogClose} sx={{ ...dashboardStyles.syncDialogCloseIcon }} />
            </Box>
            <Box
              sx={{
                ...globalStyles.display.flex,
                ...globalStyles.flex.direction.column,
                ...globalStyles.flex.align.center,
                ...globalStyles.padding(4, "Right"),
                ...globalStyles.padding(4, "Left")
              }}
            >
              <Typography
                variant='h5'
                sx={{ ...globalStyles.text.weight.bold, ...globalStyles.text.color.default}}
              >
                Sync Completed
              </Typography>
              {syncDialog.type === "error"
                ? (
                  <img
                    alt="Error Icon"
                    src={ErrorIcon}
                    style={{
                      ...dashboardStyles.syndErrorIcon
                    }}
                  />
                ): (
                  <CheckCircleOutlinedIcon
                    color="success"
                    sx={{
                      ...dashboardStyles.syncIcon,
                      ...globalStyles.margin(4, 'Top')
                    }}
                  />
                )}
              <Typography
                variant='body1'
                sx={{
                  ...(syncDialog.type === 'error' ? globalStyles.text.color.error : globalStyles.text.color.success),
                  ...globalStyles.text.weight.bold,
                  ...globalStyles.margin(4, 'Top')
                }}
              >
                {syncDialog.type === 'error' ? "Errors Detected" : "Success! Your data has been uploaded." }
              </Typography>
              <Typography
                variant='body2'
                sx={{
                  ...globalStyles.margin(2, 'Top'),
                  ...globalStyles.text.align.center
                }}
              >
                {syncDialog.type === 'error'
                  ? "Please review error list for more details on unsynched items."
                  : "Would you like to return to the home screen?"
              }
                
              </Typography>
              <Button
                onClick={handleSyncDialogClose}
                variant="contained"
                sx={{
                  ...globalStyles.margin(2, 'Top'),
                  ...globalStyles.margin(4, 'Bottom')
                }}
              >
                Close
              </Button>
            </Box>
          </DialogContent>
        </Dialog>
      )}
      {toastDialog.show && (
        <ToastDialog
          status={status}
          open={toastDialog.show}
          heading={toastDialog.heading}
          contentText={toastDialog.content}
          contentAction={toastDialog.contentAction}
          contentActionText={toastDialog.contentActionText}
          customIcon={toastDialog.customIcon}
          isError={isErrorEncountered}
          onClose={onToastClose}
        />
      )}
    </Box>
  )
};

export default DashboardView;